import type {
  GetCaseCountsRequest,
  GetCaseCountsResponse,
  OutputFieldCondition,
  SearchField,
} from '@compass/client/types/case_record';
import { useQuery } from '@tanstack/react-query';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';
import { caseRecordService } from '@/services/api';
import { useStore } from '@/store/index';

interface QuickFilter {
  name: string;
  /** Tab hides the date picker; its count uses `pinnedDates` instead of the picker range. */
  hidesDatePicker?: boolean;
  filter: {
    states?: string[];
    /**
     * Output-data conditions for PQC submitted-substate tabs. When present the
     * tab's count is computed with a dedicated getCaseCounts call (status +
     * output_conditions) rather than the shared group_by_status partition, since
     * several such tabs share `submitted` and grouping can't distinguish them.
     */
    outputConditions?: OutputFieldCondition[];
  };
}

interface BaseFilters {
  reference_ids?: string[];
  case_record_ids?: string[];
  case_codes?: string[];
  assigned_user_ids?: string[];
  created_at_after?: string;
  created_at_before?: string;
  campaign_id?: string;
  search_field?: SearchField | string;
  search_value?: string;
}

// Feature flag: When true, uses single API call with group_by_status
// When false, uses separate API calls for each filter (legacy behavior)
const USE_GROUP_BY_STATUS = true;

// Keys where non-empty arrays should be forwarded to the counts request
const ARRAY_FILTER_KEYS = [
  'case_record_ids',
  'case_codes',
  'reference_ids',
  'assigned_user_ids',
] as const;

// Keys where truthy string values should be forwarded to the counts request
const STRING_FILTER_KEYS = [
  'campaign_id',
  'created_at_after',
  'created_at_before',
  'search_field',
  'search_value',
] as const;

// Extract non-empty optional filters from baseFilters into a partial request
const extractOptionalFilters = (baseFilters: BaseFilters): Partial<GetCaseCountsRequest> => {
  const result: Partial<GetCaseCountsRequest> = {};

  for (const key of ARRAY_FILTER_KEYS) {
    const value = baseFilters[key];
    if (value?.length) {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  for (const key of STRING_FILTER_KEYS) {
    const value = baseFilters[key];
    if (value) {
      (result as Record<string, unknown>)[key] = value;
    }
  }

  return result;
};

// Build the request for getCaseCounts API
const buildCountsRequest = (
  organizationId: string,
  programId: string,
  workflowId: string,
  baseFilters: BaseFilters,
  statuses: string[],
  groupByStatus: boolean,
  outputConditions?: OutputFieldCondition[],
): GetCaseCountsRequest => ({
  organization_id: organizationId,
  program_id: programId,
  workflow_id: workflowId,
  ...extractOptionalFilters(baseFilters),
  ...(statuses.length > 0 ? { status: statuses } : {}),
  ...(groupByStatus ? { group_by_status: true } : {}),
  ...(outputConditions?.length ? { output_conditions: outputConditions } : {}),
});

/** Date bounds pinned onto tabs that hide the date picker (AQ-863). */
export type PinnedDates = Pick<BaseFilters, 'created_at_after' | 'created_at_before'>;

export const useCaseCounts = (
  workflowId: string,
  quickFilters: QuickFilter[],
  baseFilters: BaseFilters = {},
  pinnedDates?: PinnedDates,
) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useQuery({
    queryKey: [
      'quickFilterCounts',
      workflowId,
      quickFilters,
      baseFilters,
      pinnedDates,
      USE_GROUP_BY_STATUS,
    ],
    queryFn: async () => {
      if (!workflowId || !selectedOrganizationId || !selectedProgramId) return {};

      // Picker-hiding tabs count on the pinned range; the rest on the picker's.
      const filtersFor = (f: QuickFilter): BaseFilters =>
        f.hidesDatePicker && pinnedDates ? { ...baseFilters, ...pinnedDates } : baseFilters;

      try {
        if (USE_GROUP_BY_STATUS) {
          // Output-condition tabs (PQC submitted-substates) can't be split by
          // group_by_status — they get dedicated total_count calls below.
          const outputTabs = quickFilters.filter(f => !!f.filter.outputConditions?.length);
          const stateTabs = quickFilters.filter(f => !f.filter.outputConditions?.length);

          // Initialize every tab to 0 so the UI can distinguish "no data" from
          // "still loading" without nullable handling.
          const countsMap: Record<string, number> = {};
          quickFilters.forEach(filter => {
            countsMap[filter.name] = 0;
          });

          // One grouped call per distinct date range (normally one: the pinned
          // range equals the picker's until the user changes it); fan each
          // response out to the tabs that declare each status.
          const groupedByFilters = new Map<string, { filters: BaseFilters; tabs: QuickFilter[] }>();
          stateTabs.forEach(f => {
            const filters = filtersFor(f);
            const key = JSON.stringify(filters);
            (
              groupedByFilters.get(key) ??
              groupedByFilters.set(key, { filters, tabs: [] }).get(key)!
            ).tabs.push(f);
          });
          const groupedResponses = await Promise.all(
            [...groupedByFilters.values()].map(async ({ filters, tabs }) => {
              const response: GetCaseCountsResponse = await caseRecordService.getCaseCounts(
                buildCountsRequest(
                  selectedOrganizationId,
                  selectedProgramId,
                  workflowId,
                  filters,
                  [...new Set(tabs.flatMap(f => f.filter.states ?? []))],
                  true,
                ),
              );
              return { response, tabs };
            }),
          );
          for (const { response, tabs: groupedTabs } of groupedResponses) {
            const statusToFilterNames: Record<string, string[]> = {};
            groupedTabs.forEach(filter => {
              (filter.filter.states ?? []).forEach(status => {
                (statusToFilterNames[status] ??= []).push(filter.name);
              });
            });
            (response.groups ?? []).forEach(group => {
              const filterNames = group.status ? statusToFilterNames[group.status] : undefined;
              if (filterNames) {
                // Default to 0 if count is missing (protobuf omits 0 values)
                const groupCount = group.count || 0;
                filterNames.forEach(filterName => {
                  countsMap[filterName] = (countsMap[filterName] || 0) + groupCount;
                });
              }
            });
            // Filters without a status list (e.g. "All") use total_count.
            groupedTabs.forEach(filter => {
              if (!filter.filter.states || filter.filter.states.length === 0) {
                countsMap[filter.name] = response.total_count ?? 0;
              }
            });
          }

          // Output-condition tabs: one dedicated count call each (status +
          // output_conditions), reading total_count.
          if (outputTabs.length) {
            const outputCounts = await Promise.all(
              outputTabs.map(async tab => {
                const resp = await caseRecordService.getCaseCounts(
                  buildCountsRequest(
                    selectedOrganizationId,
                    selectedProgramId,
                    workflowId,
                    filtersFor(tab),
                    tab.filter.states ?? [],
                    false,
                    tab.filter.outputConditions,
                  ),
                );
                return { name: tab.name, count: resp.total_count ?? 0 };
              }),
            );
            outputCounts.forEach(({ name, count }) => {
              countsMap[name] = count;
            });
          }

          return countsMap;
        } else {
          // Legacy approach: Make separate API calls for each filter in parallel
          const countPromises = quickFilters.map(async filter => {
            const filterStatuses = filter.filter.states ?? [];
            const request = buildCountsRequest(
              selectedOrganizationId,
              selectedProgramId,
              workflowId,
              baseFilters,
              filterStatuses,
              false,
            );
            const response = await caseRecordService.getCaseCounts(request);
            return { name: filter.name, count: response.total_count || 0 };
          });

          const results = await Promise.all(countPromises);

          // Convert array to map keyed by filter name
          const countsMap: Record<string, number> = {};
          results.forEach(result => {
            countsMap[result.name] = result.count;
          });

          return countsMap;
        }
      } catch (error) {
        console.error('Failed to fetch filter counts:', error);
        return {};
      }
    },
    enabled: !!workflowId && !!selectedOrganizationId && !!selectedProgramId,
    ...CASES_QUERY_CONFIG,
  });
};
